import { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router'

import { useSessionStorage } from '@Root/utils/storageUtils'

const useScrollRestore = () => {
  const MAX_SCROLL_RESTORE_TICK_COUNT = 30
  const MAX_SCROLL_RESTORE_INTERVAL_MILLS = 100
  const MAX_SCROLL_IGNORE_USER_INTERACTION = 10

  const mainSelector = ['#myhouse_price', '#myhouse_similar_ptp_article', '#myhouse_unit_land_price', '#myhouse_unit_trend_price']
  const knownSelector = [...mainSelector]
  const mainHeaderSize = 57

  const history = useHistory()
  const { getStorage, setStorage, removeStorage } = useSessionStorage()

  const isRestoreScrollRunning = useRef(false)
  const restoreScrollTimer = useRef(0)
  const restoreScrollCount = useRef(0)

  const storeScrollY = (event?: Event) => {
    // 복원중에는 기록되지 않도록 처리
    if (isRestoreScrollRunning.current) {
      restoreScrollCount.current++
      return
    }
    setStorage(['scroll', 'store', 'path', history.location.pathname], window?.scrollY || 0)
    setStorage(['scroll', 'store', 'key', history.location.key], window?.scrollY || 0)
  }

  const onLocationChange = useCallback((location: any, action: any) => {
    // a 태그로 이동한 경우 key값이 비어있으므로 강제 생성
    // 해당 과정 중 history.listen을 통해 다시 재진입되며 최초 이동이므로 별도 동작은 수행되지 않습니다.
    if (!history.location.key) {
      location.hash && restoreScrollSelector(location.hash)
      history.replace(location)
      return
    }

    // key에 해당하는 scroll 값이 저장되어 있는 경우 기존에 진입한 페이지.
    if (action === null) {
      const restoreScrollY = Number(getStorage(['scroll', 'store', 'key', history.location.key]) || 0)
      restoreScrollPosition(restoreScrollY) || restoreScrollSelector(location.hash)
      return
    }

    // path에 해당하는 scroll 값이 저장되어 있는 경우
    if (action === 'POP') {
      const restoreScrollY = Number(getStorage(['scroll', 'store', 'path', history.location.pathname]) || 0)
      restoreScrollPosition(restoreScrollY)
      return
    }

    // path 기반일 경우 해당 path의 기존 기록 내용 제거
    if (action === 'PUSH') {
      removeStorage(['scroll', 'store', 'path', history.location.pathname])
      restoreScrollSelector(location.hash)
      return
    }
  }, [])

  const restoreScrollPosition = useCallback((scrollY: number) => {
    // 복원 대상이 0인경우 복원할 대상이 없어 종료
    if (scrollY === 0) {
      isRestoreScrollRunning.current = false
      return
    }

    restoreScrollCount.current = 0
    isRestoreScrollRunning.current = true
    let timerTickCount = 0
    restoreScrollTimer.current && window.clearTimeout(restoreScrollTimer.current)

    const restoreScrollTickFunction = () => {
      // 완료시 마지막 위치 기록처리
      if (!isRestoreScrollRunning.current || timerTickCount >= MAX_SCROLL_RESTORE_TICK_COUNT || restoreScrollCount.current > MAX_SCROLL_IGNORE_USER_INTERACTION) {
        restoreScrollTimer.current && window.clearTimeout(restoreScrollTimer.current)
        isRestoreScrollRunning.current = false
        storeScrollY()
        return false
      }

      timerTickCount++
      window.scrollTo(0, scrollY)
      restoreScrollTimer.current = window.setTimeout(restoreScrollTickFunction, MAX_SCROLL_RESTORE_INTERVAL_MILLS)
    }
    restoreScrollTimer.current = window.setTimeout(restoreScrollTickFunction, MAX_SCROLL_RESTORE_INTERVAL_MILLS)
    return true
  }, [])

  const restoreScrollSelector = useCallback((selector: string) => {
    // 이동할 대상이 없거나 미리 정의되지 않은 경우 종료
    if (!selector || !knownSelector.includes(selector)) {
      isRestoreScrollRunning.current = false
      return false
    }

    restoreScrollCount.current = 0
    isRestoreScrollRunning.current = true
    let timerTickCount = 0
    restoreScrollTimer.current && window.clearTimeout(restoreScrollTimer.current)

    const restoreScrollTickFunction = () => {
      // 완료시 마지막 위치 기록처리
      if (!isRestoreScrollRunning.current || timerTickCount >= MAX_SCROLL_RESTORE_TICK_COUNT || restoreScrollCount.current > MAX_SCROLL_IGNORE_USER_INTERACTION) {
        restoreScrollTimer.current && window.clearTimeout(restoreScrollTimer.current)
        isRestoreScrollRunning.current = false
        storeScrollY()
        return
      }

      timerTickCount++
      // 알려진 페이지의 경우 보상 스크롤 처리.
      if (mainSelector.includes(selector)) {
        const windowY = window.scrollY
        const selectorY = window.document.querySelector(selector)?.getBoundingClientRect()?.y || 0
        let scrollY = windowY + selectorY
        scrollY -= mainHeaderSize
        window.scrollTo(0, scrollY)
      } else {
        window.document.querySelector(selector)?.scrollIntoView()
      }

      restoreScrollTimer.current = window.setTimeout(restoreScrollTickFunction, MAX_SCROLL_RESTORE_INTERVAL_MILLS)
    }
    restoreScrollTimer.current = window.setTimeout(restoreScrollTickFunction, MAX_SCROLL_RESTORE_INTERVAL_MILLS)
    return true
  }, [])

  useEffect(() => {
    // auto scrollRestoration disable
    if (window.history && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }

    history.listen(onLocationChange)
    onLocationChange(history.location, null)

    // https://developer.mozilla.org/en-US/docs/Web/API/Document/scroll_event
    // iOS UIWebViews 이슈로 touch에 대해서도 처리 추가.
    // touchstart, touchend, touchcancel 의 경우 스크롤 변화와 무관하므로 이벤트 대응하지 않습니다.
    window.addEventListener('touchmove', storeScrollY)
    window.addEventListener('scroll', storeScrollY)

    return () => {
      window.removeEventListener('touchmove', storeScrollY)
      window.removeEventListener('scroll', storeScrollY)
    }
  }, [])

  return {}
}

export default useScrollRestore
